import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { useConvertToUSD } from '@/api/currency/queries/useCurrencyRates'
import { useUploadUserPkgReceipt } from '@/api/user-package/mutations/useUploadUserPkgReceipt'
import { useUserPkgs } from '@/api/user-package/queries/useUserPkgs'
import { UploadUserPkgReceiptPayload } from '@/api/user-package/types/UploadUserPkgReceiptPayload'
import { UserPkg } from '@/api/user-package/types/UserPkg'
import { AppBreadcrumbs } from '@/components/AppBreadcrumbs'
import { AppTooltip } from '@/components/AppTooltip'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { CurrencyPreview } from '@/components/CurrencyPreview'
import AttentionIcon from '@/components/icons/attention.svg?react'
import InfoIcon from '@/components/icons/info.svg?react'
import { TextField } from '@/components/TextField'
import { Button } from '@/components/ui/button'
import { queryClient } from '@/lib/queryClient'
import { QUERY_KEYS } from '@/lib/queryKeys'
import { RoutePath } from '@/RoutePath'

import { PkgPreview } from '../ClientPkg/components/PkgPreview'
import { Stepper } from './components/Stepper'
import { UploadArea } from './components/UploadArea'

export default function ReceiptUploadPage() {
  const navigate = useNavigate()
  const { mutateAsync: uploadUserPkgReceipt, isPending: isUploadingReceipt } = useUploadUserPkgReceipt()
  const { data: userPkgs } = useUserPkgs()
  const { t } = useTranslation()
  const convertToUSD = useConvertToUSD()
  const { pkgId } = useParams<{ pkgId: string }>()
  const pkg = userPkgs?.find(p => p.id === pkgId)

  // UI state
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [errorModalText, setErrorModalText] = useState('')
  const [showCustomsDeclarationModal, setShowCustomsDeclarationModal] = useState(false)
  const [verifyInfoModal, setVerifyInfoModal] = useState(false)

  // Receipt data
  const [receiptFile, setReceiptFile] = useState<File | null>(null)
  const [pkgValue, setPkgValue] = useState(pkg?.price?.toString() || '')
  const USDPrice = pkg ? convertToUSD(pkg.price, pkg.warehouse) : 0

  const getSumWarning = () => {
    if (!pkg) return ''
    const value = parseFloat(pkgValue)
    switch (pkg.warehouse) {
      case 'GR':
        if (value >= 1100) return t('receiptUpload.valueHighWarning')
        else if (value <= 40 && value !== 0) return t('receiptUpload.valueLowWarning')
        break
      case 'USA':
        if (value >= 300) return t('receiptUpload.valueHighWarning')
        else if (value <= 30 && value !== 0) return t('receiptUpload.valueLowWarning')
        break
      case 'UK':
        if (value >= 230) return t('receiptUpload.valueHighWarning')
        else if (value <= 22 && value !== 0) return t('receiptUpload.valueLowWarning')
        break
      case 'UAE':
        if (value >= 1100) return t('receiptUpload.valueHighWarning')
        else if (value <= 100 && value !== 0) return t('receiptUpload.valueLowWarning')
        break
    }
    return ''
  }

  const proceedWithUpload = async () => {
    if (!pkg) return

    try {
      const updateParams = {} as UploadUserPkgReceiptPayload
      if (receiptFile) {
        updateParams.file = receiptFile
      }
      if (pkg.price !== parseFloat(pkgValue)) {
        updateParams.price = parseFloat(pkgValue)
      }
      if (Object.keys(updateParams).length > 0) {
        updateParams.packageId = pkg.id
        await uploadUserPkgReceipt(updateParams)
      }
      console.log('Finished uploading receipt')

      // Redirect to payment page or my packages
      if (pkg.isPaid || pkg.isPhotoRequestActive || pkg.isReturnRequestActive || pkg.isShrinkRequestActive) {
        navigate(RoutePath.ClientPkg)
      } else {
        navigate(`${RoutePath.PaymentShipping}/${pkgId}`)
      }
    } catch (error) {
      console.log('Error uploading file: ', error)
      setErrorModalText(t('receiptUpload.uploadError'))
      setShowErrorModal(true)
    }
  }

  const handleSubmit = () => {
    // Check if price exists
    if (!pkgValue || parseFloat(pkgValue) < 1) {
      setErrorModalText(t('receiptUpload.packageValueRequired'))
      setShowErrorModal(true)
      return
    }

    // Check for allowed files formats
    if (!receiptFile && !pkg!.buyingReciptKey) {
      setErrorModalText(t('receiptUpload.receiptRequired'))
      setShowErrorModal(true)
      return
    }

    // Check if package value in USD is above $1000 and show customs declaration popup
    const sumInUSD = USDPrice
    if (sumInUSD > 1000) {
      setShowCustomsDeclarationModal(true)
      return
    }
    setVerifyInfoModal(true)
  }

  const getLocalCurrency = () => {
    if (!pkg) return ''
    switch (pkg.warehouse) {
      case 'GR':
        return t('receiptUpload.currencyEur')
      case 'USA':
        return t('receiptUpload.currencyUsd')
      case 'UK':
        return t('receiptUpload.currencyGbp')
      case 'UAE':
        return t('receiptUpload.currencyAed')
      default:
        return ''
    }
  }
  const onSetReceiptFile = (file: File | null) => {
    if (!file) {
      queryClient.setQueryData<UserPkg[]>([QUERY_KEYS.USER_PACKAGES], old =>
        old?.map(item => (item.id === pkg!.id ? { ...item, buyingReciptKey: '' } : item))
      )
    }
    setReceiptFile(file)
  }
  if (!pkg) {
    return null
  }

  return (
    <div className='mx-auto h-full max-w-5xl p-6'>
      <div className='mb-6'>
        <AppBreadcrumbs
          steps={[
            { label: t('clientTabs.packages'), to: RoutePath.ClientPkg },
            { label: t('payment.packageDeclaration'), to: RoutePath.ReceiptUpload },
          ]}
        />
      </div>
      <div className='mx-auto max-w-xl pb-12'>
        <div className='mb-6 flex justify-center'>
          <Stepper currentStep={0} totalSteps={3} />
        </div>

        {/* Main Content */}
        <div className='flex flex-col gap-6'>
          {/* Title Section */}
          <div className=''>
            <h1 className='text-text-primary mb-2 text-2xl font-medium'>{t('receiptUpload.receiptUploadTitle')}</h1>
            <p className='text-text-secondary text-sm leading-relaxed'>{t('receiptUpload.receiptUploadDesc')}</p>
          </div>

          {/* Package Card */}
          <PkgPreview pkg={pkg!} currentView='all' hideActions hideStatus />

          {/* Upload Section */}
          <div className='space-y-4'>
            <h3 className='text-text-primary text-sm font-medium'>{t('receiptUpload.uploadReceipt')}</h3>
            <UploadArea onChangeFile={onSetReceiptFile} previewUrl={pkg.buyingReciptKey} />
            <a href='https://www.instagram.com/p/Cao7zAmscWE/?igshid=YmMyMTA2M2Y=' target='_blank' rel='noreferrer'>
              <Button variant='link' className='gap-1'>
                <InfoIcon />
                {t('receiptUpload.whatIsMyReceipt')}
              </Button>
            </a>
          </div>

          {/* Price Input Section */}
          <div className='space-y-4'>
            <AppTooltip content={t('receiptUpload.packageValueTooltip')}>
              <div className='flex items-center gap-2'>
                <h3 className='text-text-primary text-sm font-medium'>{t('receiptUpload.whatIsPackageValue')}</h3>
                <InfoIcon />
              </div>
            </AppTooltip>
            <TextField
              trailingIcon={<CurrencyPreview warehouse={pkg.warehouse} />}
              value={pkgValue}
              type='number'
              inputMode='numeric'
              placeholder={getLocalCurrency()}
              onChange={e => setPkgValue(e.target.value)}
              supportingText={getSumWarning() || t('receiptUpload.currencyNote')}
              error={getSumWarning() !== ''}
            />
          </div>

          {/* Terms and Submit */}
          <div className='flex flex-col items-center gap-2 pt-4'>
            <Button
              onClick={handleSubmit}
              disabled={isUploadingReceipt}
              className='w-full max-w-[335px]'
              size='lg'
              loading={isUploadingReceipt}
            >
              {t('receiptUpload.continueToShippingTypeSelection')}
            </Button>

            <p className='text-text-secondary text-center text-xs'>{t('receiptUpload.verifyDeclaration')}</p>
          </div>
        </div>

        <ConfirmDialog
          isOpen={showErrorModal}
          onClose={() => setShowErrorModal(false)}
          onConfirm={() => setShowErrorModal(false)}
          confirmText={t('common.back')}
          Icon={AttentionIcon}
          title={t('payment.attention')}
          content={errorModalText}
        />
        <ConfirmDialog
          isOpen={showCustomsDeclarationModal}
          onClose={() => setShowCustomsDeclarationModal(false)}
          onConfirm={() => {
            setShowCustomsDeclarationModal(false)
            proceedWithUpload()
          }}
          confirmText={t('common.confirm')}
          cancelText={t('common.back')}
          Icon={AttentionIcon}
          title={t('receiptUpload.importantMessage')}
          content={t('receiptUpload.customsDeclarationContent')}
        />
        <ConfirmDialog
          isOpen={verifyInfoModal}
          onClose={() => setVerifyInfoModal(false)}
          onConfirm={() => {
            setVerifyInfoModal(false)
            proceedWithUpload()
          }}
          confirmText={t('receiptUpload.verifyConfirm')}
          cancelText={t('common.back')}
          Icon={AttentionIcon}
          content={t('receiptUpload.verifyContent')}
          title={t('receiptUpload.verifyTitle')}
        />
      </div>
    </div>
  )
}
